import { USER_COLOR_PALETTE } from '@paintscout/util'
import type { OptionsDocument } from 'paintscout'

/**
 * Fallback accent color derived from a company id.
 *
 * Only used when the account has no brand color of its own (see `getCompanyAccentColor`). Reuses
 * `USER_COLOR_PALETTE` — the same swatches as estimator avatars — rather than inventing colors.
 *
 * Deliberately *not* random and not stored: the same company always resolves to the same color, for
 * every user, on every device, across reloads. The hash is a plain character sum; company ids are
 * fixed-length lowercase CouchDB database names, so there's no adversarial input and nothing stronger
 * is warranted. A collision just means two accounts share a color, which costs little because the
 * name and logo sit right next to it.
 */
export function getFallbackCompanyColor(companyId?: string | null): string {
  if (!companyId) {
    return USER_COLOR_PALETTE[0]
  }

  let hash = 0
  for (let i = 0; i < companyId.length; i++) {
    hash += companyId.charCodeAt(i)
  }

  return USER_COLOR_PALETTE[hash % USER_COLOR_PALETTE.length]
}

/**
 * The accent color identifying a masqueraded account: the company's own brand primary color, or a
 * stable id-derived fallback when they haven't set one.
 *
 * Masquerade context is per-tab, so staff can now sit in several customer accounts at once, and every
 * one of those tabs renders an identical banner. Using the customer's real brand color makes "which
 * account is this?" answerable peripherally, and matches the color they see everywhere else in their
 * own account.
 *
 * Returns the raw brand value — pass it through `getCompanyAccentSurface` before using it for
 * anything visual. A brand color is arbitrary customer input, so it's never safe to place text on or
 * against it directly.
 */
export function getCompanyAccentColor({
  options,
  companyId
}: {
  options?: OptionsDocument | null
  companyId?: string | null
}): string {
  const primaryColor = options?.options?.primaryColor?.value

  if (typeof primaryColor === 'string' && primaryColor.trim()) {
    return primaryColor.trim()
  }

  return getFallbackCompanyColor(companyId)
}

/** Parses `#rgb` / `#rrggbb` into channels. Returns null for anything else. */
function parseHex(color: string): { r: number; g: number; b: number } | null {
  const hex = color.trim().replace(/^#/, '')

  if (!/^([\da-f]{3}|[\da-f]{6})$/i.test(hex)) {
    return null
  }

  const full =
    hex.length === 3
      ? hex
          .split('')
          .map((char) => char + char)
          .join('')
      : hex

  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16)
  }
}

/** Alpha applied to the brand color to produce the banner surface. */
const ACCENT_SURFACE_ALPHA = 0.14

/**
 * Turns a masqueraded company's brand color into a banner surface: a pale tint to fill with, and the
 * color itself for the border.
 *
 * Returns `null` when the color can't be parsed, which callers treat as "use the default warning
 * styling". Two reasons this is deliberately conservative rather than using the brand color directly:
 *
 * - **Legibility.** A brand color is arbitrary customer input — white, near-white, and neon all
 *   occur. Filling the banner with it at full strength makes the text unreadable for some accounts, so
 *   the fill is a low-alpha tint over the page and the full-strength color is confined to the border,
 *   where contrast doesn't matter. Text stays a dark neutral.
 * - **Blast radius.** MUI's color helpers throw on an unrecognized format, and this banner renders in
 *   the app shell on every page of a masquerade session — a malformed `primaryColor` in one account's
 *   options would take down the whole header. Parsing hex ourselves and returning null keeps a bad
 *   value cosmetic.
 */
export function getCompanyAccentSurface(accentColor?: string | null): { background: string; border: string } | null {
  if (!accentColor) {
    return null
  }

  const rgb = parseHex(accentColor)
  if (!rgb) {
    return null
  }

  return {
    background: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${ACCENT_SURFACE_ALPHA})`,
    border: accentColor.trim()
  }
}
